import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';
import { portalReportsApi } from '../../api/portal-reports';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  GlobeAltIcon,
  TrashIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import type { PortalReport } from '@affogato/shared';
import clsx from 'clsx';

/**
 * Simple markdown to HTML renderer for basic formatting
 * Supports: headings, bold, italic, lists, line breaks
 */
function renderMarkdown(content: string): string {
  return content
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headings
    .replace(/^### (.+)$/gm, '<h4 class="text-md font-semibold mt-4 mb-2 text-gray-900 dark:text-white">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-white">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white">$1</h2>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 text-gray-700 dark:text-slate-300">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul class="list-disc mb-3 pl-4">$&</ul>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p class="mb-3 text-gray-700 dark:text-slate-300">')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p class="mb-3 text-gray-700 dark:text-slate-300">')
    .replace(/$/, '</p>');
}

interface DocumentationReportProps {
  clientId: string;
  report: PortalReport;
  onUpdate: (report: PortalReport) => void;
  onDelete: () => void;
}

export function DocumentationReport({
  clientId,
  report,
  onUpdate,
  onDelete,
}: DocumentationReportProps) {
  const { t } = useTranslation('clients');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(report.title);
  const [editContent, setEditContent] = useState(report.content);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setEditTitle(report.title);
    setEditContent(report.content);
  }, [report]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await portalReportsApi.update(clientId, report.id, {
        title: editTitle,
        content: editContent,
      });
      onUpdate(response.report);
      setIsEditing(false);
      toast.success(t('reports.saved'));
    } catch (error: any) {
      toast.error(error.message || t('reports.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await portalReportsApi.publish(clientId, report.id);
      onUpdate(response.report);
      toast.success(t('reports.published'));
    } catch (error: any) {
      toast.error(error.message || t('reports.errors.publishFailed'));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('reports.deleteConfirm'))) return;

    setIsDeleting(true);
    try {
      await portalReportsApi.delete(clientId, report.id);
      onDelete();
      toast.success(t('reports.deleted'));
    } catch (error: any) {
      toast.error(error.message || t('reports.errors.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditTitle(report.title);
    setEditContent(report.content);
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          <span
            className={clsx(
              'text-xs font-medium px-2 py-1 rounded-full',
              report.status === 'published'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            )}
          >
            {report.status === 'published' ? t('reports.statusPublished') : t('reports.statusDraft')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                isLoading={isDeleting}
              >
                <TrashIcon className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isEditing ? (
          <div className="space-y-4">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2 text-lg font-semibold rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder={t('reports.titlePlaceholder')}
            />
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              placeholder={t('reports.contentPlaceholder')}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                <XMarkIcon className="w-4 h-4 mr-1" />
                {t('common:cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} isLoading={isSaving}>
                <CheckIcon className="w-4 h-4 mr-1" />
                {t('reports.saveChanges')}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {report.title}
            </h3>
            <div
              className="text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(report.content) }}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      {!isEditing && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {report.generatedAt && (
              <span>
                {t('reports.generatedAt', {
                  date: new Date(report.generatedAt).toLocaleDateString(),
                })}
              </span>
            )}
            {report.publishedAt && (
              <span className="ml-3">
                {t('reports.publishedAt', {
                  date: new Date(report.publishedAt).toLocaleDateString(),
                })}
              </span>
            )}
          </div>
          {report.status !== 'published' && (
            <Button
              size="sm"
              onClick={handlePublish}
              isLoading={isPublishing}
            >
              <GlobeAltIcon className="w-4 h-4 mr-1" />
              {t('reports.publishButton')}
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
}
